/**
 * 어느 stage 가 어느 문서를 띄우는가.
 *
 * `counselStageMachine.documents` 는 논리 이름(`counsel.progress`)만 갖는다. 실제로 그
 * 자리에 뜨는 것은 Firestore 의 v1 문서이거나(진행하기 7단계는 sub 마다 다른 문서다)
 * 아직 커스텀 React 인 화면이다. 그 대응이 지금까지는 `CounselStartPage` 의 switch 문
 * 안에만 있어서, 문서만 봐서는 상담이 어떤 순서로 무엇을 띄우는지 알 수 없었다.
 *
 * 여기 적힌 id 는 `PRODUCTION_FIXTURES` / Firestore 의 문서 id 와 같다.
 */

import type { ProgressSubStageKind } from './counselStages';

/** 아직 문서가 아닌 화면. 이관이 끝나면 이 목록은 비어야 한다. */
export type NativeScreenId =
  | 'house-structure.done'
  | 'counsel.roomPick'
  | 'counsel.movingItems'
  | 'counsel.movingTypes'
  | 'counsel.review'
  | 'counsel.complete';

export interface StageScreen {
  /** v1 FlowDocument id. 있으면 기존 InquiryFlow 가 그린다. */
  flowId?: string;
  /** 아직 문서가 아닌 화면. */
  native?: NativeScreenId;
  /** 화면 제목 — 진행 목록·디버그 표시용. */
  label: string;
  /**
   * 문서가 이 flow 로 체인하지만 호스트가 가로채는 자리.
   *
   * `counsel-start-flow` 는 문서상 `room-record-flow` 로 이어지는데, 실제 앱은 체인을
   * 막고 상담 시작 API 를 부른다(`CounselStartFlowAdapter`). 다음 화면을 정하는 건
   * 서버가 돌려준 상담이지 문서의 체인이 아니기 때문이다. 이 사실이 어댑터 코드에만
   * 있으면 문서만 봐서는 순서를 알 수 없어 여기 적어 둔다.
   */
  interceptChain?: string;
  /**
   * 이 stage 안에서 이어서 뜨는 화면.
   *
   * 방 선택은 고르고 끝나지 않는다. 고른 방의 짐 기록까지가 한 단계이고, 짐이 저장돼야
   * 남은 방 수가 줄어 다음 판정이 달라진다.
   */
  nested?: NativeScreenId;
  /**
   * 문서가 끝나도 단계가 넘어가지 않고, 종료 터미널 화면이 단계를 넘기는 자리.
   *
   * 집 구조 기록이 그렇다. 마지막 질문에 답하면 문서는 끝나지만 화면은 "방 구조가
   * 완성되었어요!" 로 남고, 거기서 "다음" 을 눌러야 스케치 세션이 열린다. 문서 종료를
   * 곧 단계 종료로 보면 이 화면이 통째로 사라진다.
   */
  terminalDriven?: boolean;
}

/** 진행하기 sub-stage → 문서. 순서는 `PROGRESS_SUB_STAGES` 가 정한다. */
export const PROGRESS_STAGE_SCREENS: Record<ProgressSubStageKind, StageScreen> = {
  'moving-date': { flowId: 'progress-moving-date', label: '이사 날짜' },
  'departure-transport': {
    flowId: 'progress-departure-transport',
    label: '출발지 운송수단',
  },
  'arrival-address': { flowId: 'progress-arrival-address', label: '도착지 주소' },
  'arrival-transport': {
    flowId: 'progress-arrival-transport',
    label: '도착지 운송수단',
  },
  packing: { flowId: 'progress-packing', label: '포장 방식' },
  'moving-types': { native: 'counsel.movingTypes', label: '이사 유형' },
  'customer-requests': {
    flowId: 'progress-customer-requests',
    label: '고객 요청사항',
  },
};

/** 진행하기를 제외한 stage → 문서. */
export const STAGE_SCREENS: Record<string, StageScreen> = {
  start: {
    flowId: 'counsel-start-flow',
    label: '상담 시작',
    interceptChain: 'room-record-flow',
  },
  'house-structure': {
    flowId: 'house-structure-record-flow',
    label: '집 구조 기록',
    terminalDriven: true,
  },
  /*
   * 방 선택만은 저장된 문서가 없다. 어떤 방이 있는지는 상담마다 다르므로 프로덕션도
   * 상담의 방 목록으로 매번 문서를 만든다(`buildRoomPickFlow`). 에디터에 남아 있는
   * `room-record-flow` 는 아이콘도 섹션도 없는 옛 초안이라 여기서 가리키지 않는다.
   */
  'pick-room': {
    native: 'counsel.roomPick',
    label: '방 선택',
    nested: 'counsel.movingItems',
  },
  'moving-items': { native: 'counsel.movingItems', label: '짐 기록' },
  'sketch-video': { label: '스케치 영상' },
  'additional-spaces': { label: '추가 공간' },
  done: { native: 'counsel.complete', label: '상담 완료' },
};
